import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';

// Week strip matched to the prototype's Timesheets weekly table:
// row dividers only (no vertical lines), today's column tinted, palette avatar.
const AVATAR_PALETTE = [
  { bg: '#fdebe6', fg: '#b94f3c' },
  { bg: '#fde6d2', fg: '#F19235' },
  { bg: '#eef5ea', fg: '#53A551' },
  { bg: '#e8eef7', fg: '#4062E5' },
  { bg: '#f5e6f7', fg: '#9333ea' },
];

function avatarColors(key) {
  const str = String(key || '');
  let h = 0;
  // eslint-disable-next-line no-bitwise
  for (let i = 0; i < str.length; i += 1) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
}

// Matches the prototype's activityColor thresholds
function activityColor(value) {
  if (value == null) return '#a8afbc';
  if (value >= 70) return '#53A551';
  if (value >= 40) return '#F19235';
  return '#b94f3c';
}

const AVATAR_CHIP = 'w-9 h-9 rounded-full flex items-center justify-center '
  + 'text-xs font-semibold flex-shrink-0';
const TOTAL_DAY_CELL = 'text-center px-3 py-4 text-sm tabular-nums text-ink-900 cursor-pointer';
const AVG_DAY_CELL = 'text-center px-3 py-4 text-sm tabular-nums font-medium cursor-pointer';
// Matches the Manual Hours swatch used on the Timesheets page.
const MANUAL_COLOR = '#213D69';
// Amber, matching the PTO treatment on the Timesheets page.
const PTO_COLOR = '#856404';
const PTO_TINT = '#fff8e1';
// Blue, matching the holiday treatment on the Timesheets page.
const HOLIDAY_COLOR = '#0d47a1';
const HOLIDAY_TINT = '#f3f9fe';
const SUB_LABEL = 'text-[11px] font-normal mt-0.5';

function ViewActivityTable({
  tableData,
  date,
  handleSelectDate,
  workspaceMember,
}) {
  const selectedDay = moment(date).day();
  // Manual entries and PTO are already folded into the day's total; the
  // sub-labels just break out how much of it came from each.
  const nonZero = (value) => !!value && value !== '0:00:00';
  const hasManual = (index) => nonZero(tableData.manualTimes?.[index]);
  const hasPto = (index) => nonZero(tableData.ptoTimes?.[index]);
  const isHoliday = (index) => !!tableData.holidayFlags?.[index];

  // Selection wins the tint so a picked day always reads as picked; then PTO,
  // which is about this member, ahead of a holiday, which is about everyone.
  const dayTint = (index, isSel) => {
    if (isSel) return undefined;
    if (tableData.ptoData?.[index]) return { backgroundColor: PTO_TINT };
    if (isHoliday(index)) return { backgroundColor: HOLIDAY_TINT };
    return undefined;
  };

  // A holiday lands on its own calendar date for everyone assigned to it, so
  // name the region it is observed in rather than implying the member's own.
  const holidayTitle = (index) => {
    const name = tableData.holidayNames?.[index];
    if (!name) return undefined;
    const zone = tableData.holidayZones?.[index];
    return zone ? `${name} (observed in ${zone})` : name;
  };
  const initials = (`${workspaceMember?.user?.firstName?.slice(0, 1) ?? ''}`
    + `${workspaceMember?.user?.lastName?.slice(0, 1) ?? ''}`).toUpperCase() || '?';
  const avatar = avatarColors(workspaceMember?.user?.email || workspaceMember?.id);
  const tint = (isSel) => (isSel ? 'bg-[#eef5ea]' : '');

  return (
    <div className="overflow-x-auto rounded-xl border border-surface-2">
      <table className="w-full min-w-[800px] border-collapse">
        <thead>
          <tr className="border-b border-surface-1">
            <th className="text-left px-5 py-2.5 align-middle min-w-[200px]">
              <div className="flex items-center gap-3">
                <span
                  className={AVATAR_CHIP}
                  style={{ backgroundColor: avatar.bg, color: avatar.fg }}
                >
                  {initials}
                </span>
                <span className="text-brand-500 font-medium truncate" style={{ lineHeight: '20px' }}>
                  {`${workspaceMember?.user?.firstName ?? ''} ${workspaceMember?.user?.lastName ?? ''}`}
                </span>
              </div>
            </th>
            {tableData.dates?.map((day, index) => {
              const isSel = selectedDay === index;
              return (
                <th
                  key={index}
                  className={`px-3 py-2.5 cursor-pointer ${tint(isSel)}`}
                  style={dayTint(index, isSel)}
                  title={holidayTitle(index)}
                  onClick={() => handleSelectDate(new Date(day))}
                >
                  <div className="flex items-center justify-center gap-2">
                    <span
                      className={`text-3xl font-semibold leading-none tabular-nums ${
                        isSel ? 'text-[#53A551]' : 'text-ink-900'
                      }`}
                    >
                      {moment(day).format('D')}
                    </span>
                    <div className="leading-tight text-left">
                      <div className={`text-[11px] font-semibold ${isSel ? 'text-[#53A551]' : 'text-ink-900'}`}>
                        {moment(day).format('ddd')}
                      </div>
                      <div className={`text-[11px] font-normal ${isSel ? 'text-[#53A551]' : 'text-ink-500'}`}>
                        {moment(day).format('MMM')}
                      </div>
                    </div>
                  </div>
                  {tableData.ptoData?.[index] && (
                    <div className="text-[10px] font-semibold mt-0.5" style={{ color: PTO_COLOR }}>
                      PTO
                    </div>
                  )}
                  {isHoliday(index) && (
                    <div
                      className="text-[10px] font-bold mt-0.5 tracking-wide"
                      style={{ color: HOLIDAY_COLOR }}
                    >
                      HOLIDAY
                    </div>
                  )}
                </th>
              );
            })}
            <th className="text-center px-5 py-2.5 text-sm font-semibold text-ink-900">
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-surface-1">
            <td className="px-5 py-4 text-sm text-ink-500">Total</td>
            {tableData.times.map((time, index) => {
              const isSel = selectedDay === index;
              return (
                <td
                  key={index}
                  className={`${TOTAL_DAY_CELL} ${tint(isSel)}`}
                  style={dayTint(index, isSel)}
                  title={holidayTitle(index)}
                  onClick={() => handleSelectDate(new Date(tableData.dates[index]))}
                  role="gridcell"
                >
                  {time}
                  {hasManual(index) && (
                    <div className={SUB_LABEL} style={{ color: MANUAL_COLOR }}>
                      {`Manual: ${tableData.manualTimes[index]}`}
                    </div>
                  )}
                  {hasPto(index) && (
                    <div className={SUB_LABEL} style={{ color: PTO_COLOR }}>
                      {`PTO: ${tableData.ptoTimes[index]}`}
                    </div>
                  )}
                  {/* Named, not added: paid holiday hours a member actually
                      worked were already credited as PTO by the server. */}
                  {isHoliday(index) && (
                    <div className={SUB_LABEL} style={{ color: HOLIDAY_COLOR }}>
                      {tableData.holidayNames?.[index]}
                      {tableData.holidayTimes?.[index] ? ` · ${tableData.holidayTimes[index]}` : ''}
                    </div>
                  )}
                </td>
              );
            })}
            <td className="text-center px-5 py-4 text-sm tabular-nums font-semibold text-ink-900">
              {tableData.totalTime}
              {nonZero(tableData.totalManualTime) && (
                <div className={SUB_LABEL} style={{ color: MANUAL_COLOR }}>
                  {`Manual: ${tableData.totalManualTime}`}
                </div>
              )}
              {nonZero(tableData.totalPtoTime) && (
                <div className={SUB_LABEL} style={{ color: PTO_COLOR }}>
                  {`PTO: ${tableData.totalPtoTime}`}
                </div>
              )}
            </td>
          </tr>
          <tr>
            <td className="px-5 py-4 text-sm text-ink-500">Avg Activity</td>
            {tableData.avgActivities.map((avgActivity, index) => {
              const isSel = selectedDay === index;
              return (
                <td
                  key={index}
                  className={`${AVG_DAY_CELL} ${tint(isSel)}`}
                  style={{ color: activityColor(avgActivity), ...dayTint(index, isSel) }}
                  onClick={() => handleSelectDate(new Date(tableData.dates[index]))}
                  role="gridcell"
                >
                  {avgActivity != null ? `${avgActivity}%` : '--%'}
                </td>
              );
            })}
            <td
              className="text-center px-5 py-4 text-sm tabular-nums font-semibold"
              style={{ color: activityColor(tableData.totalAvgActivity) }}
            >
              {tableData.totalAvgActivity != null ? `${tableData.totalAvgActivity}%` : '--%'}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

ViewActivityTable.propTypes = {
  tableData: PropTypes.shape({
    dates: PropTypes.arrayOf(PropTypes.string),
    times: PropTypes.arrayOf(PropTypes.string),
    avgActivities: PropTypes.arrayOf(PropTypes.number),
    totalTime: PropTypes.string,
    totalAvgActivity: PropTypes.number,
    manualTimes: PropTypes.arrayOf(PropTypes.string),
    totalManualTime: PropTypes.string,
    ptoTimes: PropTypes.arrayOf(PropTypes.string),
    ptoData: PropTypes.arrayOf(PropTypes.bool),
    totalPtoTime: PropTypes.string,
    holidayFlags: PropTypes.arrayOf(PropTypes.bool),
    holidayNames: PropTypes.arrayOf(PropTypes.string),
    holidayZones: PropTypes.arrayOf(PropTypes.string),
    holidayTimes: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  date: PropTypes.instanceOf(Date),
  handleSelectDate: PropTypes.func.isRequired,
  workspaceMember: PropTypes.shape({
    id: PropTypes.number.isRequired,
    user: PropTypes.shape({
      id: PropTypes.number.isRequired,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      email: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

export default ViewActivityTable;
